import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, Button, CssBaseline, Tab, Tabs, Typography } from '@mui/material';
import GallerySidePanel from '../Gallery/GallerySidePanel';
import HeaderInfo from './HeaderInfo';
import BadgeCount from './BadgeCount';
import IconViewGallery from '../../assets/icons/IconViewGallery';
import IconEmptyFlag from '../../assets/icons/IconEmptyFlag';
import getScriptHash from './hooks/getScriptHash';
import useGetLuggage from './hooks/getLuggage';
import { decodeUnzip } from '../Decryption/Decryption';
import { useDispatch, useSelector } from 'react-redux';
import Notifications from '../../ui/molecules/toast/Notifications';
import {
  getScriptSave,
  getScriptState,
  getAdditionalPhotoLabels,
  getScriptData as getCorescriptData,
  getTasksData,
  getTaskState,
  loadScript,
  LoadScriptProps,
  setAccept,
  resetCoreScript,
} from '../../redux/reducer/coreScriptReducer';
import {
  persistor,
  RootState,
  useAppSelector,
  AppDispatch,
} from '../../redux/Store';
import {
  ActivatedByMapper,
  SaveDataToResults,
} from '@safeguardproperties/corescript-react-npm';
import { v4 as uuidv4 } from 'uuid';

import StationsList from './stations';
import TaskControl from '../Tasks/TaskControl';
import TaskSidePanel from '../Tasks/TaskSidePanel';
import useDecision from './hooks/UseDecision';
import {
  filterData,
  useVisulizationCountInStation,
} from '../../utils/helpers/formatValue';
import {
  SaveData,
  ScriptData,
  ScriptResultsAccess,
  ScriptResultsAnswer,
  ScriptResultsWorkOrder,
  ScriptResultsAnswerMediaGroup,
  ScriptResultsAnswerMedia,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import useSaveData from './hooks/useSaveData';
import CustomTypography from '../../ui/atoms/CustomTypography';
import {
  resetContextId,
  setContextId,
  setIsGalleryPanelOpen,
  setIsCountPanelOpen,
  setQueryObj,
  setViewFlagged,
  setIsReadOnly,
  setLoadedFirstTime,
} from '../../redux/reducer/contextIdReducer';
import {
  useSinglePostMessage,
  cancelPostMessage,
} from '../../utils/helpers/usePostMessage';
import useImages from './hooks/useImages';
import {
  resetState,
  toggleAllQuestions,
} from '../../redux/reducer/selectedQuestionReducer';
import { PersistHelper } from '../../redux/persistHelper';
import SkeletonLoader from '../../ui/molecules/skeleton/SkeletonLoader';
import { Platform } from '../../constants/platform';
import { useLocation } from 'react-router';
import { decryptJSON } from '../../utils/helpers/Encryption';
import {
  setLuggageData,
  setOrder,
} from '../../redux/reducer/inspact/order_reducer';
import { contextValue } from '../../constants';
import useQueryObject from '../../hooks/useQueryObject';
import FinsihedDialog from '../../ui/atoms/FinishedDialog';
import ForbiddenDialog from '../../ui/atoms/ForbiddenDialog';
import { setShow403Dialog } from '../../redux/reducer/forbiddenDetectorReducer';
import SidePanel from './SidePanel';

const closeWindow = (context: string, key: string, id: string) => {
  cancelPostMessage(context, key, id); // Send cancel message before closing
  window.close();
};

// Small orange incomplete-count circle for the Tasks/Bids tabs, sized to match
// the side-panel badges. Renders nothing at zero.
const TabIncompleteBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '20px',
        height: '20px',
        padding: '0 5px',
        backgroundColor: '#F35625',
        borderRadius: '50%',
        color: '#fff',
        fontSize: '11px',
        fontWeight: 600,
        fontFamily: 'Montserrat',
      }}
    >
      {count}
    </Box>
  ) : null;
function MainComponent() {
  const dispatch = useDispatch<AppDispatch>();
  const scrollToStationRef = useRef<((index: number) => void) | null>(null);

  const show403Dialog = useSelector(
    (state: RootState) => state.forbiddenDetector.show403Dialog
  );
  const loadedfirstTime = useSelector(
    (state: RootState) => state.contextId.loadedfirstTime
  );
  const timestamp = useAppSelector((state) => state.coreScript.scriptTimestamp);
  // const { state } = useAppSelector((state) => state.coreScript.coreScript);
  const stateData = useSelector(getScriptState);
  const taskState = useSelector(getTaskState);
  const saveData = useSelector(getScriptSave);
  const additionalPhotoLabels = useSelector(getAdditionalPhotoLabels);

  const scriptData = useSelector(getCorescriptData);
  const tasksData = useSelector(getTasksData);
  const isAudit = useSelector((state: RootState) => state.contextId.isAudit);

  const currentOrder = useAppSelector(
    (state) => state.inspactOrder.currentOrder
  );
  const { contextId, queryObj } = useSelector(
    (state: RootState) => state.contextId
  );
  const sidePanelOpen = useSelector(
    (state: RootState) => state.contextId.isGalleryPanelOpen
  );
  const countSidePanelOpen = useSelector(
    (state: RootState) => state.contextId.isCountPanelOpen
  );

  const showSubmitDataError = useSelector(
    (state: RootState) => state.contextId.showSubmitDataError
  );
  const viewFlagged = useSelector(
    (state: RootState) => state.contextId.viewFlagged
  );
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );
  const checkboxState = useSelector(
    (state: RootState) => state.selectedQuestion
  );

  const {
    isAuditResultsBySubmissionId,
    isContextSupportGallery,
    isContextSupportSidePanel,
    isContextSidePanelOpenByDefault,
    isContextSupportTasksBids,
    isContextSupportAddTasksBids,
    readOnlyAfterSubmit,
    saveWorkInfoWithSubmit,
    allowAddtionalImageLabels,
    orderNumber,
    propertyLoanId,
    submissionId,
  } = useQueryObject();

  const [isExpanded, setIsExpanded] = useState(false);

  const [mainTab, setMainTab] = useState<'questions' | 'tasks' | 'bids'>(
    'questions'
  );

  // const [viewFlagged, setViewFlagged] = useState(false); // New state for flagged view

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [labelImages, setLabelImages] = useState<(File | null)[][]>(
    new Array(12).fill([]).map(() => [])
  );

  const { keyValues } = useAppSelector((state) => state.language);
  const {
    getScriptData,
    isGetScriptPending,
    isGetScriptLanguagePackPending,
    isErrorScript,
    getTaskScriptData,
    isGetTaskScriptPending,
    isTaskScriptSettled,
  } = getScriptHash();

  const { luggageData, isLuggagehDataFetching, isLuggageError } =
    useGetLuggage();

  const {
    postSubmitData,
    isSubmitDataPending,
    successfulSubmitData,
    submitDataError,
  } = useSaveData();

  const {
    SeededData,
    isSeededFetching,
    isErrorCoreScriptInitData,
    auditFailures,
    isAuditFailuresFetching,
    currentAnswers,
    isCurrentAnswersFetching,
    priorDecisions,
    isPriorDecisionsFetching,
    decisionReasons,
    isDecisionReasonsFetching,
  } = useDecision();

  const { fetchImages } = useImages();

  if (
    orderNumber &&
    propertyLoanId &&
    submissionId &&
    currentOrder?.orderNumber !== orderNumber &&
    currentOrder?.propertyLoanId !== propertyLoanId
  ) {
    dispatch(
      setOrder({ orderNumber: orderNumber, propertyLoanId: propertyLoanId })
    );
    dispatch(setContextId(submissionId));
    dispatch(
      setQueryObj({
        context: contextValue?.AuditResultsBySubmissionId,
        key: 'Submission.ID',
        id: submissionId,
      })
    );
  }

  const stations = useMemo(() => {
    if (isAudit) {
      const filtered = filterData(stateData?.Stations, viewFlagged, saveData);

      return filtered;
    }

    return stateData?.Stations;
  }, [saveData, stateData?.Stations, viewFlagged, isAudit]);

  const count = useVisulizationCountInStation(stations);

  useEffect(() => {
    String.prototype.toLanguagePack = function (): string {
      const key = String(this);
      if (keyValues && keyValues[key]) {
        return keyValues[key];
      }

      // HOTFIX: This is just a hot fix until we figure out how we want to handle this label ( as it is not part of the script )
      if (key === 'WorkOrder.FTVForm.Photo') {
        return 'FTV Form';
      }

      return key;
    };
  }, [keyValues]);

  useEffect(() => {
    if (isContextSidePanelOpenByDefault) {
      dispatch(setIsCountPanelOpen(true)); // Open the side panel by default
      dispatch(setIsGalleryPanelOpen(false)); // Ensure the gallery panel is closed
    }
  }, [isContextSidePanelOpenByDefault, dispatch]);

  useSinglePostMessage(stateData);

  const handleCloseSidePanel = () => {
    dispatch(setIsGalleryPanelOpen(false));
    dispatch(setIsCountPanelOpen(false));
    setIsExpanded(false);
  };

  const handleExpandPanel = (expand: boolean) => {
    setIsExpanded(expand);
  };

  const handleToggleFlaggedView = () => {
    dispatch(setViewFlagged(!viewFlagged));
  };

  const handleSubmit = () => {
    let wr = {} as ScriptResultsWorkOrder;
    if (saveWorkInfoWithSubmit) {
      wr = {
        Script: saveData.ScriptHash,
        ScriptVersion: saveData.ScriptVersion,
        OrderNumber: orderNumber,
      } as ScriptResultsWorkOrder;
    }

    const sr = SaveDataToResults(
      scriptData as ScriptData,
      saveData as SaveData,
      {} as ScriptResultsAccess,
      wr,
      tasksData,
      uuidv4
    );

    if (allowAddtionalImageLabels) {
      const groups = {
        Name: 'Safeguard.Media.UpdateDescriptions',
        Values: ['NA'] as string[],
      } as ScriptResultsAnswer;
      groups.MediaGroup = [] as ScriptResultsAnswerMediaGroup[];
      for (const label of additionalPhotoLabels) {
        if (label.Images && label.Images.length > 0) {
          const Guids = { GUID: [] } as ScriptResultsAnswerMedia;
          const labeltrans = label.Label.toLanguagePack();
          const v = {
            Type: 'Photo',
            Label: labeltrans,
            Media: [],
          } as ScriptResultsAnswerMediaGroup;
          for (const image of label.Images) {
            Guids.GUID.push(image.guid);
          }
          v.Media.push(Guids);
          groups.MediaGroup.push(v);
        }
      }
      sr.Result.Answers.push(groups);
    }

    // Remove answers with "Select Option"
    sr.Result.Answers = sr.Result.Answers.filter(
      (answer) => answer.Values[0] !== 'Select Option'
    );

    postSubmitData({
      parameter: sr,
      AuditSaved: stateData?.AuditSaved,
      Followup: stateData?.Followup,
    })
      .then(async (result) => {
        await persistor.purge();
        if (readOnlyAfterSubmit) {
          dispatch(setIsReadOnly(true));
        }
        dispatch(resetContextId());
      })
      .catch((error) => {
        Notifications(error?.message, 0);
      });
  };

  const handleToggleAllQuestions = () => {
    const isChecked = !checkboxState.allSelected; // Toggle based on current state
    const stationsMapped = stations?.map((station: any) => ({
      title: station.title,
      questionKeys: station?.data
        ?.filter(
          (q: any) =>
            !q?.AuditDecision?.DecisionId &&
            q?.Medallions !== undefined &&
            saveData?.Answers[q?.Title]?.Answers?.length > 0 &&
            saveData?.Answers[q?.Title]?.Answers.some(
              (answer: any) => answer?.Answer !== ''
            )
        )
        ?.map((q: any) => q.QuestionKey),
    }));

    dispatch(toggleAllQuestions({ stations: stationsMapped, isChecked })); // Use the renamed variable
  };

  const checkForDisabling = () => {
    if (isReadOnly) {
      return true; // Disable if `isReadOnly` is true
    }

    return stations?.every((station: any) =>
      station?.data?.every(
        (q: any) => q?.AuditDecision?.DecisionId || q?.Medallions === undefined
      )
    );
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getAllQuestions = (stations: { [key: string]: string[] }) => {
    // Use Object.values to extract all arrays and flatten them
    return Object.values(stations).flat();
  };

  const handleAcceptAll = () => {
    dispatch(
      setAccept({
        questionPaths: getAllQuestions(checkboxState?.stations),
      })
    );
    dispatch(resetState());
  };

  const loadData = useCallback(() => {
    if (
      SeededData &&
      luggageData &&
      getScriptData &&
      getScriptData.scriptValueGzip &&
      getScriptData.labelsGzip &&
      ((isAudit && decisionReasons) || isAudit === false) &&
      ((isAudit && priorDecisions) || isAudit === false) &&
      ((isAudit && currentAnswers) || isAudit === false) &&
      // Wait for the task-library fetch to settle so the engine can build the
      // TaskList during load. Settles immediately for contexts without tasks.
      isTaskScriptSettled &&
      loadedfirstTime === false
    ) {
      try {
        const decoded = decodeUnzip(getScriptData?.scriptValueGzip);
        const decodedLabels = decodeUnzip(getScriptData?.labelsGzip);

        let taskScriptData: string | undefined;
        if (isContextSupportTasksBids && getTaskScriptData?.scriptValueGzip) {
          try {
            // decodeUnzip returns a parsed object; LoadScript expects a JSON
            // string (it JSON.parses it internally). No ActivatedByMapper —
            // the engine maps the task script during load.
            taskScriptData = JSON.stringify(
              decodeUnzip(getTaskScriptData.scriptValueGzip)
            );
          } catch (error) {
            console.log('failed to decode task script', error);
          }
        }

        const mapping = ActivatedByMapper(JSON.stringify(decoded));
        const paersedMapping = JSON.parse(mapping);
        const payload = {
          scriptHash: getScriptData?.scriptHash,
          scriptData: mapping,
          luggageData: luggageData,
          seededData: JSON.stringify(SeededData),
          medallionData: isAudit ? JSON.stringify(auditFailures) : undefined,
          auditCurrentData: isAudit
            ? JSON.stringify(currentAnswers)
            : undefined,
          auditDecisionsData: isAudit
            ? JSON.stringify(priorDecisions)
            : undefined,
          decisionReasonsData: isAudit
            ? JSON.stringify(decisionReasons)
            : undefined,
          additionalLabelList: JSON.stringify(decodedLabels),
          taskScriptData: taskScriptData,
        } as LoadScriptProps;

        const parsedLuggageData = JSON.parse(luggageData);
        const luggageStateData =
          parsedLuggageData?.Luggages ?? parsedLuggageData;

        dispatch(setLuggageData(luggageStateData));
        dispatch(loadScript(payload));
        dispatch(setIsGalleryPanelOpen(!isAudit));
        dispatch(setLoadedFirstTime(true));
      } catch (error) {
        console.log('exception', error);
      }
    }
  }, [
    luggageData,
    getScriptData,
    SeededData,
    isAudit,
    auditFailures,
    currentAnswers,
    priorDecisions,
    decisionReasons,
    loadedfirstTime,
    isTaskScriptSettled,
    getTaskScriptData,
    isContextSupportTasksBids,
    dispatch,
  ]);

  // Check for errors based on the conditions
  const hasError = useMemo(() => {
    return (
      isErrorScript ||
      isErrorCoreScriptInitData ||
      isLuggageError ||
      showSubmitDataError
    );
  }, [
    showSubmitDataError,
    isErrorScript,
    isLuggageError,
    isErrorCoreScriptInitData,
  ]);

  const showSpiner = useMemo(() => {
    return (
      isAuditFailuresFetching ||
      isCurrentAnswersFetching ||
      isGetScriptPending ||
      isGetScriptLanguagePackPending ||
      isLuggagehDataFetching ||
      isSeededFetching ||
      isPriorDecisionsFetching ||
      isGetTaskScriptPending ||
      isSubmitDataPending
    );
  }, [
    isAuditFailuresFetching,
    isCurrentAnswersFetching,
    isGetScriptPending,
    isGetScriptLanguagePackPending,
    isLuggagehDataFetching,
    isSeededFetching,
    isPriorDecisionsFetching,
    isGetTaskScriptPending,
    isSubmitDataPending,
  ]);
  const isskeletonLoader =
    Platform?.isAudit() && globalThis.self !== globalThis.top;

  const { uploadQueue, cloneQueue } = useAppSelector((state) => state.gallery);

  const shouldFetchImages = useMemo(() => {
    const isUploadQueueCompleted =
      uploadQueue?.length > 0
        ? uploadQueue.every(
            (item) =>
              item.status === 'success' ||
              item.status === 'failed' ||
              item.status === 'rejected'
          )
        : true;
    const isCloneQueueEmpty = cloneQueue.length === 0;
    return isUploadQueueCompleted && isCloneQueueEmpty;
  }, [uploadQueue, cloneQueue]);

  useEffect(() => {
    if (show403Dialog) {
      return;
    }
    if (contextId && shouldFetchImages) {
      fetchImages().catch((error) => {
        Notifications(error?.message, 0);
      });
    }
  }, [fetchImages, shouldFetchImages, show403Dialog, contextId]);

  useEffect(() => {
    if (show403Dialog) {
      return;
    }
    if (isErrorScript || isErrorCoreScriptInitData || isLuggageError) {
      dispatch(resetState());
      dispatch(resetCoreScript());
      PersistHelper.clearAllData();
      return;
    }

    loadData();
  }, [
    luggageData,
    getScriptData,
    isErrorScript,
    isErrorCoreScriptInitData,
    isLuggageError,
    show403Dialog,
    loadData,
    dispatch,
  ]);

  // Added-instance counts split by mode, shown on the Tasks/Bids tab labels.
  // incompleteTaskCount drives the orange badge: non-bid tasks not yet both
  // script- and media-complete.
  const { bidCount, taskCount, incompleteTaskCount, incompleteBidCount } =
    useMemo(() => {
      const bidModes = new Set(['BidProposal', 'OrderedBidProposal']);
      let bid = 0;
      let task = 0;
      let incompleteTask = 0;
      let incompleteBid = 0;
      for (const item of Object.values(taskState ?? {})) {
        const isComplete = !!(item?.scriptComplete && item?.mediaComplete);
        if (item?.modeType && bidModes.has(item.modeType)) {
          bid += 1;
          if (!isComplete) {
            incompleteBid += 1;
          }
        } else {
          task += 1;
          if (!isComplete) {
            incompleteTask += 1;
          }
        }
      }
      return {
        bidCount: bid,
        taskCount: task,
        incompleteTaskCount: incompleteTask,
        incompleteBidCount: incompleteBid,
      };
    }, [taskState]);

  // Every task instance must also be script- and media-complete before submit
  // is allowed (the main script's ScriptComplete/MediaComplete don't cover the
  // per-task state the engine tracks separately in taskState).
  const allTasksComplete = Object.values(taskState ?? {}).every(
    (task) => task?.scriptComplete && task?.mediaComplete
  );

  const shouldSubmitBeDisabled = () => {
    return (
      isSubmitDataPending ||
      isReadOnly ||
      !(
        stateData?.Followup ||
        (stateData?.ScriptComplete &&
          stateData?.MediaComplete &&
          allTasksComplete)
      )
    );
  };

  if (showSpiner) {
    return (
      <>
        {isskeletonLoader ? (
          <SkeletonLoader width='100%' height={100} variant='text' count={5} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: 2,
              flex: '1',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <img src='/gifs/safeview.gif' alt='Loading...' />
          </Box>
        )}
      </>
    );
  } else if (
    successfulSubmitData &&
    queryObj?.context === contextValue?.INSPI
  ) {
    return (
      <FinsihedDialog>
        Order completed. Click finish to close window
      </FinsihedDialog>
    );
  } else if (hasError && !show403Dialog) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Typography variant='h6'>
          {submitDataError || showSubmitDataError
            ? `Unexpected error has occurred: ${submitDataError?.message ?? ''}`
            : 'Order not ready, please try again in 15 minutes'}
        </Typography>
      </Box>
    );
  }

  // if (!isErrorScript && isLuggageError)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1, // Takes all available space dynamically
        padding: 2,
        backgroundColor: '#BFBFBF0D',
        height: '100vh', // Take full height of the viewport
        overflow: 'hidden', // Prevents unnecessary scrollbars
      }}
    >
      {timestamp && (
        <CustomTypography
          variant='body1'
          sx={{
            color: 'black',
            fontFamily: 'Montserrat',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            textTransform: 'capitalize',
            marginX: '0.5rem',
          }}
        >
          Script Date - {timestamp}
          <br />
          {orderNumber ? `Order #: ${orderNumber}` : null}
        </CustomTypography>
      )}
      <ForbiddenDialog>
        You’ve been inactive for a while or your session has timed out. Please
        log back in to continue where you left off.
      </ForbiddenDialog>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Left section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginY: '20px',
            // hides: show403Dialog ? 'none' : 'flex',
          }}
        >
          <HeaderInfo luggageData={luggageData}></HeaderInfo>
          <BadgeCount count={count}></BadgeCount>
        </Box>

        {/* Right section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {isAudit ? (
            <Button
              variant='text'
              sx={{
                display: 'flex',
                padding: '10px 0px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
                alignSelf: 'stretch',
              }}
              onClick={handleToggleFlaggedView} // Toggle flagged view
            >
              <IconEmptyFlag />
              <Typography
                variant='body1'
                sx={{
                  color: '#343330',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px' /* 200% */,
                }}
              >
                {viewFlagged ? 'View All' : 'View Flagged'}
              </Typography>
            </Button>
          ) : (
            !isAuditResultsBySubmissionId && (
              <Button
                variant='outlined'
                sx={{
                  display: 'flex',
                  width: '98px',
                  padding: '10px 12px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                  alignSelf: 'stretch',
                  borderRadius: '48px',
                  border: '1px solid #E3E3E3',
                  background: '#FFF',
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    cursor: 'not-allowed',
                    pointerEvents: 'all !important', // Override pointer-events: none
                  },
                }}
                onClick={() =>
                  closeWindow(
                    queryObj?.context || '',
                    queryObj?.key || '',
                    queryObj?.id || ''
                  )
                }
                disabled={Platform?.isInspect()}
              >
                <Typography
                  variant='body1'
                  sx={{
                    color: Platform?.isInspect() ? 'white' : '#343330',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    lineHeight: '28px' /* 200% */,
                  }}
                >
                  {saveWorkInfoWithSubmit && isReadOnly ? 'Finished' : 'Cancel'}
                </Typography>
              </Button>
            )
          )}
          {isAudit && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                sx={{
                  color: '#343330',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px',
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'all !important', // Override pointer-events: none
                  },
                }}
                onClick={handleToggleAllQuestions}
                disabled={checkForDisabling()}
              >
                {checkboxState.allSelected ? 'Uncheck All' : 'Check All'}
              </Button>
              <Button
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '28px',
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'all !important', // Override pointer-events: none
                  },
                }}
                disabled={!checkboxState?.allSelected}
                onClick={handleAcceptAll}
              >
                Accept All
              </Button>
            </Box>
          )}
          {!isAuditResultsBySubmissionId && (
            <Button
              variant='contained'
              sx={{
                display: 'flex',
                width: '98px',
                padding: '10px 12px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                alignSelf: 'stretch',
                borderRadius: '48px',
                border: '1px solid #E3E3E3',
                backgroundColor: '#53b50a',
                '&:hover': {
                  backgroundColor: '#388007',
                },
              }}
              disabled={shouldSubmitBeDisabled()}
              onClick={handleSubmit}
            >
              <Typography
                variant='body1'
                sx={{
                  color: '#FFF',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '28px' /* 200% */,
                }}
              >
                Submit
              </Typography>
            </Button>
          )}
        </Box>
      </Box>

      {(isContextSupportGallery || isContextSupportSidePanel) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='text'
            sx={{
              padding: '10px 12px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              alignSelf: 'stretch',
              visibility: isContextSupportGallery ? 'visible' : 'hidden',
            }}
            onClick={() => {
              dispatch(setIsCountPanelOpen(false));
              dispatch(setIsGalleryPanelOpen(!sidePanelOpen));
            }}
          >
            <IconViewGallery />
            <Typography
              variant='body1'
              sx={{
                color: '#1C6EC9',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '28px' /* 200% */,
              }}
            >
              {sidePanelOpen ? 'Hide Galley' : 'View Gallery'}
            </Typography>
          </Button>
          <Button
            variant='text'
            sx={{
              padding: '10px 12px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              alignSelf: 'stretch',
              visibility:
                isContextSupportSidePanel || mainTab !== 'questions'
                  ? 'visible'
                  : 'hidden',
            }}
            onClick={() => {
              dispatch(setIsCountPanelOpen(!countSidePanelOpen));
              dispatch(setIsGalleryPanelOpen(false));
            }}
          >
            <IconViewGallery />
            <Typography
              variant='body1'
              sx={{
                color: '#1C6EC9',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '28px' /* 200% */,
              }}
            >
              {countSidePanelOpen ? 'Hide Side Panel' : 'View Side Panel'}
            </Typography>
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: '10px',
          overflow: 'hidden', // Prevent extra scrolling
        }}
      >
        <CssBaseline />

        {!isExpanded && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0, // Allow shrinking so wide task content doesn't squeeze the gallery
              // flexGrow: sidePanelOpen ? 0.99 : 1, // Adjust width dynamically
              // width: sidePanelOpen ? 'calc(100vh - 376px)' : '100%', // Reduce width when side panel is open
              overflowY: 'auto', // Enables vertical scrolling
              maxHeight: Platform?.isAudit()
                ? 'calc(100vh - 5rem)'
                : 'calc(100vh - 11rem)', // Ensure it doesn't exceed viewport height
            }}
          >
            {isContextSupportTasksBids ? (
              <>
                <Tabs
                  value={mainTab}
                  onChange={(_event, value) => setMainTab(value)}
                  aria-label='questions and tasks tabs'
                  sx={{ minHeight: '48px', marginBottom: '12px' }}
                >
                  <Tab
                    label='Questions'
                    value='questions'
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  />
                  <Tab
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        Tasks ({taskCount})
                        <TabIncompleteBadge count={incompleteTaskCount} />
                      </Box>
                    }
                    value='tasks'
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  />
                  <Tab
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        Bids ({bidCount})
                        <TabIncompleteBadge count={incompleteBidCount} />
                      </Box>
                    }
                    value='bids'
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  />
                </Tabs>
                <Box
                  sx={{ display: mainTab === 'questions' ? 'block' : 'none' }}
                >
                  <StationsList
                    stations={stations}
                    onScrollToStation={(scrollToStation) => {
                      scrollToStationRef.current = scrollToStation; // Store the function
                    }}
                  />
                </Box>
                <Box sx={{ display: mainTab === 'tasks' ? 'block' : 'none' }}>
                  <TaskControl
                    mode='tasks'
                    allowAdd={isContextSupportAddTasksBids}
                  />
                </Box>
                <Box sx={{ display: mainTab === 'bids' ? 'block' : 'none' }}>
                  <TaskControl
                    mode='bids'
                    allowAdd={isContextSupportAddTasksBids}
                  />
                </Box>
              </>
            ) : (
              <StationsList
                stations={stations}
                onScrollToStation={(scrollToStation) => {
                  scrollToStationRef.current = scrollToStation; // Store the function
                }}
              />
            )}
          </Box>
        )}
        {isContextSupportGallery && sidePanelOpen && (
          <GallerySidePanel
            open={sidePanelOpen}
            onClose={handleCloseSidePanel}
            onExpand={handleExpandPanel}
            expanded={isExpanded}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            labelImages={labelImages}
            setLabelImages={setLabelImages}
            allowAddtionalImageLabels={allowAddtionalImageLabels}
            hideLabelTabs={mainTab !== 'questions'}
          />
        )}
        {countSidePanelOpen &&
          (mainTab === 'tasks' || mainTab === 'bids' ? (
            <TaskSidePanel mode={mainTab} onClose={handleCloseSidePanel} />
          ) : (
            isContextSupportSidePanel && (
              <SidePanel
                onClose={handleCloseSidePanel}
                onNavigateToStation={(index) => {
                  if (scrollToStationRef.current) {
                    scrollToStationRef.current(index); // Call the scrollToStation function
                  }
                }}
              />
            )
          ))}
      </Box>
    </Box>
  );

  // return (
  //   <Box
  //     sx={{
  //       display: 'flex',
  //       flexDirection: 'column',
  //       alignItems: 'center',
  //       justifyContent: 'center',
  //       height: '100vh',
  //     }}
  //   >
  //     <Typography variant='h6'>Unable to load the component.</Typography>
  //   </Box>
  // );
}

export default MainComponent;
